import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface Credits {
  quota: number;
  used: number;
  remaining: number;
  plan: 'free' | 'pro' | 'premium';
  next_reset_at?: string;
}

export const useCredits = () => {
  const [credits, setCredits] = useState<Credits | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchCredits = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setCredits(null);
        setLoading(false);
        return;
      }

      // Use account-summary edge function for consistent data
      const { data, error } = await supabase.functions.invoke('account-summary');

      if (error) {
        console.error("Error fetching credits:", error);
        toast({
          title: "Error",
          description: "Failed to load credits",
          variant: "destructive",
        });
        return;
      }

      setCredits(data);
    } catch (error) {
      console.error("Error fetching credits:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCredits();

    // Subscribe to credits ledger changes
    const channel = supabase
      .channel("credits-changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "credits_ledger",
        },
        () => {
          fetchCredits();
        }
      )
      .subscribe();

    // Listen for manual refresh events
    const handleCreditsUpdate = () => fetchCredits();
    window.addEventListener('creditsUpdated', handleCreditsUpdate);

    return () => {
      supabase.removeChannel(channel);
      window.removeEventListener('creditsUpdated', handleCreditsUpdate);
    };
  }, []);

  return { credits, loading, refetch: fetchCredits };
};
